import React, { useMemo, useState } from "react";
import WidgetCard from "@/components/controls/WidgetCard";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Bullet } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Spinner from "@/components/ui/spinner";
import Dropdown from "@/components/controls/Dropdown/Dropdown";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { transformPipelineData } from "./helpers";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import PipelineValueChart from "./components/PipelineValueChart";
import {
  PaginationDirection,
  PipelineMetric,
  PipelineOverTimeData,
  PipelinePeriod
} from "@/components/layouts/PipelineOverTime/types";

const TRANSLATION_PREFIX = "pipelineOverTime";

export type PipelineOverTimePropsType = {
  data?: PipelineOverTimeData;
  isLoading?: boolean;
  period?: PipelinePeriod;
  onPeriodChange?: (period: PipelinePeriod) => void;
  metric?: PipelineMetric;
  onMetricChange?: (metric: PipelineMetric) => void;
  onNavigate?: (direction: PaginationDirection) => void;
  canGoPrev?: boolean;
  canGoNext?: boolean;
};

const PipelineOverTime: React.FC<PipelineOverTimePropsType> = ({
  isLoading,
  data,
  period = PipelinePeriod.MONTHLY,
  onPeriodChange,
  metric = PipelineMetric.VALUE,
  onMetricChange,
  onNavigate,
  canGoPrev = false,
  canGoNext = false
}) => {
  const { t } = useTranslation();
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [isTransitioning, setIsTransitioning] = useState(false);

  const { label: arrMrrLabel } = useArrMrr();

  const isDealsMetric = metric === PipelineMetric.DEALS;

  const periodOptions = {
    monthly: t(`${TRANSLATION_PREFIX}.month`),
    quarterly: t(`${TRANSLATION_PREFIX}.quarter`)
  };

  const metricOptions = {
    value: arrMrrLabel,
    deals: t(`${TRANSLATION_PREFIX}.dealAmount`)
  };

  const handlePeriodChange = (value: string) => {
    if (onPeriodChange) {
      onPeriodChange(value as PipelinePeriod);
    }
  };

  const handleMetricChange = (value: string) => {
    if (onMetricChange) {
      onMetricChange(value as PipelineMetric);
    }
  };

  const filteredData = useMemo(
    () => data?.pipelineOverTime?.dataPoints || [],
    [data]
  );

  const chartData = useMemo(() => {
    if (filteredData.length === 0) return [];
    return transformPipelineData(filteredData, period);
  }, [filteredData, period]);

  const yearRangeLabel = useMemo(() => {
    if (filteredData.length === 0) return "";
    const firstYear = filteredData[0].year;
    const lastYear = filteredData[filteredData.length - 1].year;

    if (firstYear === lastYear) {
      return firstYear.toString();
    }
    return `${firstYear}/${lastYear.toString().slice(-2)}`;
  }, [filteredData]);

  const handleNavigate = (direction: PaginationDirection) => {
    if (onNavigate) {
      setIsTransitioning(true);
      setTimeout(() => {
        onNavigate(direction);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 50);
      }, 200);
    }
  };

  const handleMouseMove = (e: any) => {
    if (e && e.activeTooltipIndex !== undefined && e.activeCoordinate) {
      setTooltipPos({
        x: e.activeCoordinate.x - 80,
        y: 20
      });
    }
  };

  const pipelineData = data?.pipelineOverTime;

  return (
    <WidgetCard
      cardHeight="472px"
      title={t(`${TRANSLATION_PREFIX}.title`)}
      feedBackButtonIdContext="pipeline-over-time"
      cardClassName="gap-4"
    >
      <div className="flex h-full w-full">
        <div className="flex flex-col gap-6 w-full h-full">
          <div className="py-1 flex gap-3 justify-between items-center">
            <div className="flex gap-3">
              <div className="flex items-center">
                <IconContainer size="5" className="p-[5px] text-cta">
                  <Bullet />
                </IconContainer>
                <Text variant="bodySecondary">
                  {t(`${TRANSLATION_PREFIX}.actualPipeline`)}
                </Text>
              </div>
              <div className="flex items-center">
                <IconContainer
                  size="5"
                  className="p-[5px] text-charts-purple-sat"
                >
                  <Bullet />
                </IconContainer>
                <Text variant="bodySecondary">
                  {t(`${TRANSLATION_PREFIX}.forecastedPipeline`)}
                </Text>
              </div>
            </div>
            <div className="flex gap-2">
              <Dropdown
                label={t(`${TRANSLATION_PREFIX}.metric`)}
                options={metricOptions}
                defaultValue={metric}
                value={metric}
                valueSelected={handleMetricChange}
                className="min-w-[120px]"
                dataTest="pipeline-metric-dropdown"
              />
              <Dropdown
                label={t(`${TRANSLATION_PREFIX}.showBy`)}
                options={periodOptions}
                defaultValue={period}
                value={period}
                valueSelected={handlePeriodChange}
                className="min-w-[120px]"
                dataTest="pipeline-period-dropdown"
              />
            </div>
          </div>
          <div className="flex flex-col gap-2 h-full">
            <div
              className="flex w-full relative transition-opacity duration-200 flex-1"
              style={{
                opacity: isTransitioning ? 0 : 1
              }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center w-full h-[250px]">
                  <Spinner size="lg" />
                </div>
              ) : pipelineData && chartData.length > 0 ? (
                <PipelineValueChart
                  chartData={chartData}
                  isDealsMetric={isDealsMetric}
                  tooltipPos={tooltipPos}
                  onMouseMove={handleMouseMove}
                />
              ) : (
                <div className="flex items-center justify-center h-full w-full">
                  <Text variant="bodySecondary">
                    {t(`${TRANSLATION_PREFIX}.noData`)}
                  </Text>
                </div>
              )}
            </div>
            <div className="flex items-center justify-center gap-4 px-4 flex-shrink-0">
              <button
                onClick={() => handleNavigate(PaginationDirection.PREV)}
                disabled={!canGoPrev}
                className={`p-2 rounded-md transition-colors flex-shrink-0 ${
                  canGoPrev
                    ? "hover:bg-surface-hover text-primary cursor-pointer"
                    : "text-secondary opacity-30 cursor-not-allowed"
                }`}
                aria-label={
                  period === PipelinePeriod.MONTHLY
                    ? "Previous year"
                    : "Previous year"
                }
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <Text variant="smLabel">{yearRangeLabel}</Text>
              <button
                onClick={() => handleNavigate(PaginationDirection.NEXT)}
                disabled={!canGoNext}
                className={`p-2 rounded-md transition-colors flex-shrink-0 ${
                  canGoNext
                    ? "hover:bg-surface-hover text-primary cursor-pointer"
                    : "text-secondary opacity-30 cursor-not-allowed"
                }`}
                aria-label={
                  period === PipelinePeriod.MONTHLY ? "Next year" : "Next year"
                }
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </WidgetCard>
  );
};

export default PipelineOverTime;
